import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { normalizeOptions, type SelectOption } from "@/lib/service-detail";

/**
 * Type-aware input for one custom field. Used everywhere we render or
 * collect custom-field values — Inventory item create + edit, Client
 * create + edit, eventually booking flow too.
 *
 * The `field` shape only requires `type` and `options` (for SELECT
 * fields). Both `customer` + `inventory` value DTOs satisfy this.
 */
export type CustomFieldShape = {
  type: string;
  options?: unknown;
  required?: boolean;
};

export function CustomFieldInput({
  field,
  value,
  onChange,
}: {
  field: CustomFieldShape;
  value: string;
  onChange: (next: string) => void;
}) {
  switch (field.type) {
    case "textarea":
      return (
        <textarea
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      );
    case "number":
      return (
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "date":
      return (
        <Input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "checkbox":
      return (
        <Switch
          checked={value === "true"}
          onCheckedChange={(b) => onChange(b ? "true" : "")}
        />
      );
    case "select": {
      const opts: SelectOption[] = normalizeOptions(field.options);
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    case "file":
    case "text":
    default:
      return (
        <Input value={value} onChange={(e) => onChange(e.target.value)} />
      );
  }
}
